import { GetUserList } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";
import {
  enumPlanRoles,
  enumShareRoles,
  kUserPlanRoles,
  kUserShareRoles,
  kROUser,
  kAdminUser,
  kRWUser
} from "@coreapp/constants";
import {
  Avatar,
  Flex,
  Icon,
  Menu,
  Text,
  TooltipWrapper,
  calculateUserColor
} from "@theorgchartdev/storybook";
import React, { Fragment } from "react";
import cx from "classnames";
import { GetWSMgr } from "@/Utils/StateLib";
import { wouldRemoveLastOwner, type PlanUser } from "@/Utils/Plan/planOwnerHelpers";

export enum SharingMode {
  plan = "plan",
  filter = "filter"
}

export type UserLabelProps = {
  showRoleSelect: boolean;
  role: string;
  editorRole?: string;
  name: string;
  userID: string;
  owner: string;
  users?: PlanUser[];
  changeUserRoleCallback?: Function;
  handleDisableExistingUser?: Function;
  disableOptions: boolean;
  availableRoles?: readonly string[];
  restrictToRole?: string;
  allowROUserInteraction?: boolean;
  roleTriggerTestId?: string;
};

export type User = {
  username: string;
  name: string;
  email: string;
  role: string;
  group: string;
  editorRole?: string;
};

export type GroupLabelProps = {
  name: string;
  countLabel?: string;
};

export class ShareWithHandler {
  owner: string = "";
  sharingMode: SharingMode = SharingMode.plan;

  private userList: User[] = [];
  private groupList: any = [];
  private userOptionsList: any[] = [];
  private groupOptionsList: any[] = [];
  private allOptionsList: any[] = [];
  private groupsUsers: { [id: string]: User[] } = {};

  constructor(mode: SharingMode = SharingMode.plan) {
    this.owner = GetWSMgr()?.GetUserName() ?? "";
    this.sharingMode = mode;
  }

  async loadUsersAndGroups() {
    const { userList, groupInfo } = await GetUserList();

    this.userList = userList;
    this.groupList = groupInfo.groups;
  }

  private buildEmployeesOptions(
    selectedUsers: string[],
    callback: (users: User[]) => void,
    includeOwner: boolean = false
  ) {
    let userOptions: any[] = [];

    if (!this.userList) return [];

    this.userList.forEach(user => {
      const isOwner = user["username"] === this.owner;
      const isROUser = user["role"] === kROUser;
      const isAlreadySelected = selectedUsers.indexOf(user["username"]) > -1;
      if ((isOwner && !includeOwner) || isAlreadySelected) return;

      let userID = user["username"];
      let displayName = userID;

      if (user["name"]) displayName = user["name"];
      else if (user["email"]) displayName = user["email"];

      // In filter mode, ROUsers are selectable but forced to viewer role
      const isDisabled = isROUser && this.sharingMode === SharingMode.plan;

      userOptions.push({
        value: userID,
        content: (
          <UserLabel
            showRoleSelect={false}
            role={user["role"]}
            name={displayName}
            owner={this.owner}
            userID={userID}
            disableOptions={false}
            allowROUserInteraction={this.sharingMode === SharingMode.filter}
          />
        ),
        clickHandler: isDisabled
          ? (_: any, e: Event) => {
              e.stopPropagation();
            }
          : () => {
              if (isROUser && this.sharingMode === SharingMode.filter) {
                callback([{ ...user, editorRole: enumShareRoles.viewer }]);
              } else {
                callback([user]);
              }
            },
        dataObj: user,
        disabled: isDisabled,
        className: isDisabled ? "ocn-ro-user-tag-share" : ""
      });
    });

    return userOptions;
  }

  private buildGroupOptions(callback: (users: User[]) => void) {
    let groupOptions: any[] = [];

    if (!this.groupList || this.groupList?.length === 0) return groupOptions;

    let groupNames = Object.values(this.groupList).map((group: any) => group.name);

    if (groupNames.indexOf("Full Access") === -1) {
      let validFullAccessUsers = this.getGroupUsers("0");
      let fullAccessCount = validFullAccessUsers.length;
      let fullAccessCountLabel =
        fullAccessCount === 0
          ? appstr("(No people)")
          : `(${fullAccessCount}  ${appstr(fullAccessCount === 1 ? "person" : "people")})`;
      groupOptions.push({
        value: "0",
        dataObj: { isGroup: true, groupID: "0" },
        clickHandler: () => {
          callback(this._applyGroupUserRoles(this.getGroupUsers("0")));
        },
        label: appstr("fullaccess"),
        content: <GroupLabel name={appstr("fullaccess")} countLabel={fullAccessCountLabel} />
      });
    }

    Object.keys(this.groupList).forEach(groupId => {
      const group = this.groupList[groupId];
      let validUsersInGroup = this.getGroupUsers(groupId);
      let usersInGroupCount = 0;
      if (validUsersInGroup.length > 0) usersInGroupCount = validUsersInGroup.length;

      let groupCountLabel =
        usersInGroupCount === 0
          ? appstr("(No people)")
          : `(${usersInGroupCount}  ${appstr(usersInGroupCount === 1 ? "person" : "people")})`;

      groupOptions.push({
        value: groupId,
        dataObj: { isGroup: true, groupID: groupId },
        clickHandler: () => {
          callback(this._applyGroupUserRoles(validUsersInGroup));
        },
        label: group.name,
        content: <GroupLabel name={group.name} countLabel={groupCountLabel} />
      });
    }, this);

    return groupOptions;
  }

  buildUserAndGroupOptions = (
    selectedUsers: any[],
    callback: (entity: any[]) => void,
    includeOwner: boolean = false
  ) => {
    const employees = this.buildEmployeesOptions(selectedUsers, callback, includeOwner);
    const groups = this.buildGroupOptions(callback);

    this.userOptionsList = employees;
    this.groupOptionsList = groups;
    this.allOptionsList = employees.concat(groups);

    return this.allOptionsList;
  };

  getShareWithOptions = (queryToUse: string, selectedUsers: any[], usersOnly: boolean = false) => {
    if (this.allOptionsList.length === 0 || !queryToUse) return [];
    const selectedUsersIds = selectedUsers.map(user => user.username);

    const validUsers = this.userOptionsList.filter((option: any) => {
      const fieldsToCheck = ["name", "email", "username"];
      const userAlreadySelected = selectedUsersIds.indexOf(option.dataObj.username) !== -1;
      const queryIncludesUser = fieldsToCheck.some(field => {
        const fieldString = option.dataObj[field] || "";
        return fieldString.toLowerCase().includes(queryToUse.toLowerCase());
      });
      return !userAlreadySelected && queryIncludesUser;
    });

    const validGroups = this.groupOptionsList.filter((option: any) => {
      const groupName = option.label || "";
      return groupName.toLowerCase().includes(queryToUse.toLowerCase());
    });

    if (validUsers.length === 0) {
      validUsers.push({
        enableHoverBackground: false,
        disabled: true,
        content: (
          <Text size={"xsmall"} modifier={"medium"} colorModifier={"faintgrey"}>
            {appstr("nomatches")}
          </Text>
        ),
        value: "unselectable_option-no-results-users",
        className: "ocn-share-with-no-results-tag"
      });
    }

    if (usersOnly) return validUsers;

    if (validGroups.length === 0) {
      validGroups.push({
        enableHoverBackground: false,
        disabled: true,
        content: (
          <Text size={"xsmall"} modifier={"medium"} colorModifier={"faintgrey"}>
            {appstr("nomatches")}
          </Text>
        ),
        value: "unselectable_option-no-results-groups",
        className: "ocn-share-with-no-results-tag"
      });
    }

    return [
      {
        enableHoverBackground: false,
        label: appstr("Users"),
        content: (
          <Text modifier={"medium"} size={"xsmall"}>
            {appstr("Users")}
          </Text>
        ),
        disabled: true,
        value: "unselectable_option-employees-tag",
        className: "ocn-share-with-title-tag"
      },
      ...validUsers,
      {
        enableHoverBackground: false,
        label: appstr("Groups"),
        content: (
          <Text modifier={"medium"} size={"xsmall"}>
            {appstr("Groups")}
          </Text>
        ),
        disabled: true,
        value: "unselectable_option-groups-tag",
        className: "ocn-share-with-title-tag"
      },
      ...validGroups
    ];
  };

  getGroupUsers = (groupID: string) => {
    if (this.groupsUsers[groupID]) return this.groupsUsers[groupID];

    const groupUsers = this.userList.filter((user: any) => {
      if (user["username"] === this.owner || user["group"] !== groupID) return false;

      if (this.sharingMode === SharingMode.filter) {
        // Filter mode: include all roles (Admin, RW, and RO)
        return true;
      }
      // Plan mode: only Admin/RW
      return user["role"] === kAdminUser || user["role"] === kRWUser;
    });

    this.groupsUsers[groupID] = groupUsers;
    return groupUsers;
  };

  private _applyGroupUserRoles = (users: User[]): User[] => {
    if (this.sharingMode !== SharingMode.filter) return users;

    return users.map(u => (u.role === kROUser ? { ...u, editorRole: enumShareRoles.viewer } : u));
  };

  buildActiveSharedUserOptions = (
    selectedUsers: any[],
    changeUserRoleCallback: Function,
    handleDisableExistingUser: Function,
    roleTriggerTestId?: string
  ) => {
    let activeUserOptions: any[] = [];

    const usersForValidation: PlanUser[] = selectedUsers
      .filter(u => !u.isGroup)
      .map(u => ({
        name: u.username,
        role: u.editorRole || enumPlanRoles.collaborator
      }));

    selectedUsers.forEach(user => {
      if (user.isGroup) return;
      const userID = user.username;
      const editorRole = user.editorRole;
      const userInfoIdx = this.userList.findIndex((user: any) => user.username === userID);

      if (userInfoIdx < 0) return;
      if (
        editorRole !== enumPlanRoles.collaborator &&
        editorRole !== enumPlanRoles.viewer &&
        editorRole !== enumPlanRoles.owner
      )
        return;

      const userInfo = this.userList[userInfoIdx];
      const systemRole = userInfo.role;
      let displayName = user.name || user.email || user.username;

      const isFilterMode = this.sharingMode === SharingMode.filter;
      const isTargetROUser = systemRole === kROUser;
      const availableRoles = isFilterMode ? kUserShareRoles : undefined;
      const restrictToRole = isFilterMode && isTargetROUser ? enumShareRoles.viewer : undefined;

      activeUserOptions.push({
        value: userID,
        content: (
          <UserLabel
            disableOptions={userID === this.owner}
            showRoleSelect={true}
            role={systemRole}
            editorRole={editorRole}
            name={displayName}
            userID={userID}
            owner={this.owner}
            users={usersForValidation}
            changeUserRoleCallback={changeUserRoleCallback}
            handleDisableExistingUser={handleDisableExistingUser}
            availableRoles={availableRoles}
            restrictToRole={restrictToRole}
            allowROUserInteraction={isFilterMode}
            roleTriggerTestId={roleTriggerTestId}
          />
        )
      });
    }, this);

    return activeUserOptions;
  };

  static formatExistingUsers = (users: any[]) => {
    return users.map((user: any) => ({
      ...user,
      editorRole: user.role || enumPlanRoles.collaborator,
      username: user.name,
      name: user.displayName || user.name
    }));
  };
}

export const UserLabel = (props: UserLabelProps) => {
  let displayName = props.name;
  let avatarLabel = displayName ? displayName[0].toUpperCase() : "";
  let avatarColor = calculateUserColor(avatarLabel);
  let owner = props.owner;

  const buildRoleAndAccessOptions = () => {
    let options = [];
    const users = props.users || [];
    const roles = props.availableRoles || kUserPlanRoles;

    for (let role of roles) {
      const isRestricted = !!props.restrictToRole && role !== props.restrictToRole;

      // Only apply wouldRemoveLastOwner logic for plan roles (not filter sharing)
      const wouldRemoveLast =
        !props.availableRoles &&
        role !== enumPlanRoles.owner &&
        wouldRemoveLastOwner(users, props.userID, role);

      options.push({
        label: appstr(role),
        value: role,
        clickHandler:
          wouldRemoveLast || isRestricted
            ? undefined
            : () => props.changeUserRoleCallback?.(props.userID, role),
        divider: !props.availableRoles && role === enumPlanRoles.owner,
        disabled: wouldRemoveLast || isRestricted
      });
    }

    const canRemove =
      props.userID !== owner &&
      (props.availableRoles ? true : !wouldRemoveLastOwner(users, props.userID));

    if (canRemove) {
      options.push({
        label: appstr("Remove access"),
        value: props.userID,
        clickHandler: () => props.handleDisableExistingUser?.(props.userID)
      });
    }
    return options;
  };

  const blockROUser = props.role === kROUser && !props.allowROUserInteraction;
  const Wrapper = blockROUser ? TooltipWrapper : Fragment;
  const wrapperProps = blockROUser
    ? {
        tooltipProps: {
          message: appstr("collabplanalert")
        }
      }
    : {};
  const divProps = blockROUser
    ? {
        onClick: (e: React.MouseEvent<HTMLDivElement, Event>) => {
          e.stopPropagation();
        }
      }
    : {};

  return (
    <Wrapper {...wrapperProps}>
      <Flex
        gap={"8px"}
        alignItems={"center"}
        padding={"4px"}
        className={"oc-name-column"}
        divProps={divProps}
      >
        <Avatar text={avatarLabel} isTriggerButton={false} color={avatarColor} size={"small"} />
        <Text size={"xsmall"} modifier={"medium"}>
          {displayName}
        </Text>
        {props.showRoleSelect && (
          <div className={cx("oc-menu-small-text ", "oc-right-justify")}>
            <Menu
              buttonSize={"small"}
              label={appstr(props.editorRole || props.role)}
              value={props.editorRole || props.role}
              options={buildRoleAndAccessOptions()}
              triggerProps={{ disabled: props.disableOptions, size: "small" }}
              dataTestId={props.roleTriggerTestId}
              popoverProps={{
                floatingProps: {
                  "data-testid": props.roleTriggerTestId
                    ? `${props.roleTriggerTestId}-popover`
                    : "share-role-trigger-popover"
                } as Record<string, string>
              }}
            />
          </div>
        )}
      </Flex>
    </Wrapper>
  );
};

export const GroupLabel = (props: GroupLabelProps) => {
  const displayText = props.countLabel ? `${props.name} ${props.countLabel}` : props.name;
  return (
    <Flex gap={"8px"} alignItems={"center"} padding={"4px"}>
      <Icon name={"users"} size={"xsmall"} modifier={"default"} />
      <Text size={"xsmall"} modifier={"medium"}>
        {displayText}
      </Text>
    </Flex>
  );
};
